import React from "react";
import { formatTextWithBullets } from "../../../../utils/formatTextUtils";

const ResumePreview = ({ resumeData }) => {
  return (
    <div className="resume-preview-container">
      <div className="preview-header">
        <h2 className="preview-title">Resume Preview</h2>
        <div className="preview-actions">
          <button className="preview-button">
            <svg
              className="w-4 h-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
              />
            </svg>
            Export PDF
          </button>
        </div>
      </div>

      <div className="preview-content">
        <div className="resume-paper">
          <div className="tae-resume">
            {/* Main Column */}
            <div className="resume-main-column">
              {/* Header */}
              <div className="resume-header">
                <h1 className="resume-name">
                  {resumeData.header.name || "YOUR NAME"}
                </h1>
                <div className="resume-title">
                  {resumeData.header.subtitle || "Professional Title"}
                </div>
                <div className="resume-tagline">
                  {resumeData.header.tagline || "Your professional tagline"}
                </div>
                <div className="contact-info">
                  {resumeData.header.contactInfo?.map((contact, idx) => (
                    <span key={`contact-${idx}`}>
                      {contact.value}
                      {idx < resumeData.header.contactInfo.length - 1 && " • "}
                    </span>
                  ))}
                </div>
              </div>

              {/* Main Content Sections */}
              {resumeData.mainContent?.map((section, sectionIndex) => (
                <div
                  key={section.id || `main-section-${sectionIndex}`}
                  className="main-section"
                >
                  <h3>{section.title}</h3>

                  {/* Profile Section */}
                  {section.type === "profile" && (
                    <div className="profile-content">
                      {formatTextWithBullets(section.content)}
                    </div>
                  )}

                  {/* Experience Section */}
                  {section.type === "experience" && (
                    <div className="experience-list">
                      {section.items?.map((item, itemIndex) => {
                        // Debug logging
                        console.log("=== Experience Item Debug ===");
                        console.log("Item:", item);
                        console.log("Raw details:", item.details);
                        console.log("Details type:", typeof item.details);
                        console.log("Details length:", item.details?.length);
                        if (item.details) {
                          console.log(
                            "Details includes newline:",
                            item.details.includes("\n")
                          );
                          console.log(
                            "Details JSON:",
                            JSON.stringify(item.details)
                          );
                        }

                        return (
                          <div
                            key={item.id || `exp-${itemIndex}`}
                            className="job-item"
                          >
                            <div className="job-title">
                              {item.jobTitle || "Position Title"}
                            </div>
                            <div className="company-info">
                              {item.companyInfo || "Company • Location • Dates"}
                            </div>
                            <div className="details">
                              {formatTextWithBullets(item.details, true)}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Volunteer Section */}
                  {section.type === "volunteer" && (
                    <div className="volunteer-list">
                      {section.items?.map((item, itemIndex) => (
                        <div
                          key={item.id || `vol-${itemIndex}`}
                          className="job-item"
                        >
                          <div className="job-title">
                            {item.role || "Volunteer Role"}
                          </div>
                          <div className="company-info">
                            {item.organization || "Organization • Dates"}
                          </div>
                          <div className="details">
                            {formatTextWithBullets(item.details, true)}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Projects Section */}
                  {section.type === "projects" && (
                    <div className="projects-list">
                      {section.items?.map((item, itemIndex) => (
                        <div
                          key={item.id || `proj-${itemIndex}`}
                          className="job-item"
                        >
                          <div className="job-title">
                            {item.title || "Project Title"}
                          </div>
                          <div className="company-info">
                            {item.technologies || "Technologies Used"}
                          </div>
                          <div className="details">
                            {formatTextWithBullets(item.description, true)}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Achievements Section */}
                  {section.type === "achievements" && (
                    <ul className="achievements-list">
                      {section.items?.map((item, itemIndex) => (
                        <li key={item.id || `achieve-${itemIndex}`}>
                          <strong>{item.title || "Achievement"}</strong>
                          {item.description && `: ${item.description}`}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>

            {/* Sidebar Column */}
            <div className="resume-sidebar-column">
              {resumeData.sidebarContent?.map((section, sectionIndex) => (
                <div
                  key={section.id || `sidebar-section-${sectionIndex}`}
                  className="sidebar-section"
                >
                  <h3>{section.title}</h3>

                  {/* Education Section */}
                  {section.type === "education" && (
                    <div className="education-list">
                      {section.items?.map((item, itemIndex) => (
                        <div
                          key={item.id || `edu-${itemIndex}`}
                          className="edu-item"
                        >
                          <div className="edu-degree">
                            {item.degree || "Degree"}
                          </div>
                          <div className="edu-institution">
                            {item.institution || "Institution"}
                          </div>
                          <div className="edu-period">
                            {item.period || "Period"}
                          </div>
                          {item.details && (
                            <div className="edu-details">
                              {formatTextWithBullets(item.details, true)}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Skills Sections */}
                  {["competencies", "technical", "interests"].includes(
                    section.type
                  ) && (
                    <div className="sidebar-list">
                      <ul>
                        {section.items?.map((item, idx) => (
                          <li key={`${section.type}-${idx}`}>
                            {typeof item === "string"
                              ? item
                              : item.name || "Skill"}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Languages Section */}
                  {section.type === "languages" && (
                    <div className="sidebar-list">
                      <ul>
                        {section.items?.map((item, idx) => (
                          <li key={`lang-${idx}`}>
                            {item.name || "Language"}
                            {item.proficiency && ` (${item.proficiency})`}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Certifications Section */}
                  {section.type === "certifications" && (
                    <div className="sidebar-list">
                      <ul>
                        {section.items?.map((item, itemIndex) => (
                          <li key={item.id || `cert-${itemIndex}`}>
                            {item.name || "Certification"}
                            {item.period && ` - ${item.period}`}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Awards Section */}
                  {section.type === "awards" && (
                    <div className="awards-list">
                      {section.items?.map((item, itemIndex) => (
                        <div
                          key={item.id || `award-${itemIndex}`}
                          className="award-item"
                        >
                          <div className="award-name">
                            {item.name || "Award"}
                            {item.year && ` (${item.year})`}
                          </div>
                          {item.description && (
                            <div className="award-description">
                              {item.description}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Publications Section */}
                  {section.type === "publications" && (
                    <div className="publications-list">
                      {section.items?.map((item, itemIndex) => (
                        <div
                          key={item.id || `pub-${itemIndex}`}
                          className="publication-item"
                        >
                          <div className="publication-title">
                            {item.title || "Publication Title"}
                          </div>
                          <div className="publication-info">
                            {item.publisher || "Publisher"}
                            {item.year && `, ${item.year}`}
                          </div>
                          {item.description && (
                            <div className="publication-description">
                              {item.description}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Social Impact Section */}
                  {section.type === "social" && (
                    <div className="social-list">
                      {section.items?.map((item, itemIndex) => (
                        <div
                          key={item.id || `social-${itemIndex}`}
                          className="social-item"
                        >
                          <div className="social-title">
                            {item.title || "Initiative"}
                          </div>
                          {item.organization && (
                            <div className="social-org">
                              {item.organization}
                            </div>
                          )}
                          {item.description && (
                            <div className="social-description">
                              {formatTextWithBullets(item.description, true)}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResumePreview;
